import { useEffect, useState } from 'react';
import { gql } from '@yi/core';

import { ContentId, utilities } from '~/common';

import type { EventCallback } from './types';

import { isNearMediaEnd } from './isNearMediaEnd';
import { useMediaPlayer } from './MediaPlayerProvider';
import { useGetCurrentUserMediaPosition } from './useGetCurrentUserMediaPosition';
import { useSaveMediaPosition } from './useSaveMediaPosition';

function extractQueryStringTime(): number {
  if (utilities.isSSR()) {
    return 0;
  }
  const searchParams = new URLSearchParams(window.location.search);
  const parsedTime = Number(searchParams.get('t'));

  if (isNaN(parsedTime)) {
    return 0;
  }

  return parsedTime;
}

export function useRestorePosition({
  contentId,
  contentType,
  getTimeToSet = lastSavedTime => lastSavedTime,
}: {
  contentId: ContentId;
  contentType: gql.typesClient.MediaPositionContentType;
  getTimeToSet?: (lastSavedTime: number) => number;
}) {
  const { player } = useMediaPlayer();

  const savedPosition = getTimeToSet(useGetCurrentUserMediaPosition(contentId, contentType));

  const queryStringTime = extractQueryStringTime();

  const isQueryStringTimeExist = queryStringTime > 0;

  const currentTimeToSet = isQueryStringTimeExist ? queryStringTime : savedPosition;

  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    if (player !== null && currentTimeToSet > 0 && !isRestored) {
      setIsRestored(true);

      player.ready(() => {
        player.currentTime(currentTimeToSet);
      });
    }
    return;
  }, [currentTimeToSet, isRestored, player, savedPosition]);
}

export function useSavePosition({
  contentId,
  contentType,
  shouldSaveNearTheEnd = false,
}: {
  contentId: ContentId;
  contentType: gql.typesClient.MediaPositionContentType;
  /*
     by default we stop tracking media position close to the end (last 5% of the media)
     For series episodes we calculate completion by the media position, so we want to send media position until the end
  */
  shouldSaveNearTheEnd?: boolean;
}) {
  const { savePosition: saveMediaPosition, forceSavePosition } = useSaveMediaPosition(
    contentId,
    contentType,
  );
  const { player } = useMediaPlayer();

  useEffect(() => {
    if (player !== null) {
      const seekHanlder: EventCallback = () => {
        const currentTime = player.currentTime();
        const duration = player.duration();

        if ((shouldSaveNearTheEnd || !isNearMediaEnd(currentTime, duration)) && currentTime > 0) {
          forceSavePosition(currentTime);
        }
      };
      const timeHandler: EventCallback = () => {
        const currentTime = player.currentTime();
        const duration = player.duration();
        if (shouldSaveNearTheEnd || !isNearMediaEnd(currentTime, duration)) {
          saveMediaPosition(currentTime);
        }
      };

      player.on('seek', seekHanlder);
      player.on('timeupdate', timeHandler);
      return () => {
        player.off('seek', seekHanlder);
        player.off('timeupdate', timeHandler);
      };
    }
    return;
  }, [contentType, forceSavePosition, player, saveMediaPosition, shouldSaveNearTheEnd]);
}

type Props = {
  // For some reason this contentId is different from the mediaId
  contentId: ContentId;
  contentType: gql.typesClient.MediaPositionContentType;
};
export const SaveAndRestorePosition: React.FC<Props> = props => {
  useSavePosition(props);
  useRestorePosition(props);
  return null;
};
